import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download, Volume2 } from 'lucide-react';
import WaveformBars from './WaveformBars';

const AudioPlayer = ({ audioUrl, audioUrls, voiceName, textPreview }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);

  const currentSrc = audioUrl || (audioUrls && audioUrls[0]) || '';
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // Determine if this is a local merged audio (starts with /api/audio/)
  const fullSrc = currentSrc.startsWith('/api/') ? `${BACKEND}${currentSrc}` : currentSrc;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !fullSrc) return;
    audio.src = fullSrc;
    audio.load();
  }, [fullSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); } else { audio.play().catch(() => {}); }
  };

  const handleDownload = async () => {
    const token = localStorage.getItem('vf_token');
    try {
      const downloadUrl = currentSrc.startsWith('/api/')
        ? `${BACKEND}${currentSrc}`
        : `${BACKEND}/api/tts/proxy-audio?url=${encodeURIComponent(currentSrc)}`;
      
      const resp = await fetch(downloadUrl, {
        headers: currentSrc.startsWith('/api/') ? {} : { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `voiceralab-${(voiceName || 'audio').replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(fullSrc, '_blank');
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div data-testid="audio-player" className="glass-card rounded-2xl p-5 space-y-4 border border-border">
      <audio ref={audioRef} preload="metadata" />

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <Volume2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-foreground truncate">{voiceName}</p>
          {textPreview && <p className="text-xs text-muted-foreground line-clamp-1">{textPreview}</p>}
        </div>
      </div>

      {/* Waveform */}
      <WaveformBars isPlaying={isPlaying} count={30} height={40} />

      {/* Progress */}
      <div className="space-y-1">
        <div
          className="h-1.5 bg-secondary rounded-full overflow-hidden cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            if (audioRef.current && duration) {
              audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
            }
          }}
        >
          <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          data-testid="play-pause-btn"
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg shadow-blue-500/25"
        >
          {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </button>

        <div className="flex-1 flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <input type="range" min="0" max="1" step="0.05" value={volume}
            onChange={(e) => { const v = parseFloat(e.target.value); setVolume(v); if (audioRef.current) audioRef.current.volume = v; }}
            className="flex-1 h-1 accent-blue-500"
          />
        </div>

        <button
          data-testid="download-audio-btn"
          onClick={handleDownload}
          className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center hover:bg-secondary/70 transition-colors"
          title="Download audio"
        >
          <Download className="w-4 h-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

export default AudioPlayer;
